import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import * as React from 'react';

import { isUnauthorized } from '@/api/http-error';
import { isPublicPath } from '@/auth/public-paths';

import { PingClientProvider } from '@/api/ping-client-context';
import type { PingClient } from '@/api/ping-client';
import { ParametersClientProvider } from '@/api/parameters-client-context';
import type { ParametersClient } from '@/api/parameters-client';
import { IdentityClientProvider } from '@/api/identity-client-context';
import type { IdentityClient } from '@/api/identity-client';
import { LegalClientProvider } from '@/api/legal-client-context';
import type { LegalClient } from '@/api/legal-client';
import { LegalAdminClientProvider } from '@/api/legal-admin-client-context';
import type { LegalAdminClient } from '@/api/legal-admin-client';
import { SupplierClientProvider } from '@/api/supplier-client-context';
import type { SupplierClient } from '@/api/supplier-client';
import { CampaignClientProvider } from '@/api/campaign-client-context';
import type { CampaignClient } from '@/api/campaign-client';
import { ExportsClientProvider } from '@/api/exports-client-context';
import type { ExportsClient } from '@/api/exports-client';
import { RateCollectionClientProvider } from '@/api/rate-collection-client-context';
import type { RateCollectionClient } from '@/api/rate-collection-client';
import { OperationalViewClientProvider } from '@/api/operational-view-client-context';
import type { OperationalViewClient } from '@/api/operational-view-client';
import { AdminActivationAlertsClientProvider } from '@/api/admin-activation-alerts-client-context';
import type { AdminActivationAlertsClient } from '@/api/admin-activation-alerts-client';
import { PositioningClientProvider } from '@/api/positioning-client-context';
import type { PositioningClient } from '@/api/positioning-client';
import { RankAdminClientProvider } from '@/api/rank-admin-client-context';
import type { RankAdminClient } from '@/api/rank-admin-client';
import { CampaignAnalysisClientProvider } from '@/api/campaign-analysis-client-context';
import type { CampaignAnalysisClient } from '@/api/campaign-analysis-client';
import { PublicationClientProvider } from '@/api/publication-client-context';
import type { PublicationClient } from '@/api/publication-client';
import { NegotiationClientProvider } from '@/api/negotiation-client-context';
import type { NegotiationClient } from '@/api/negotiation-client';
import { SupplierDashboardClientProvider } from '@/api/supplier-dashboard-client-context';
import type { SupplierDashboardClient } from '@/api/supplier-dashboard-client';
import { SupplierHistoryClientProvider } from '@/api/supplier-history-client-context';
import type { SupplierHistoryClient } from '@/api/supplier-history-client';
import { SupplierAnnouncementsClientProvider } from '@/api/supplier-announcements-client-context';
import type { SupplierAnnouncementsClient } from '@/api/supplier-announcements-client';
import { SupplierConditionsClientProvider } from '@/api/supplier-conditions-client-context';
import type { SupplierConditionsClient } from '@/api/supplier-conditions-client';
import { SupplierTeamClientProvider } from '@/api/supplier-team-client-context';
import type { SupplierTeamClient } from '@/api/supplier-team-client';
import { SupplierMeClientProvider } from '@/api/supplier-me-client-context';
import type { SupplierMeClient } from '@/api/supplier-me-client';
import {
  ChatAdminClientProvider,
  ChatSupplierClientProvider,
} from '@/api/chat-client-context';
import type { ChatClient } from '@/api/chat-client';
import { NotificationsClientProvider } from '@/api/notifications-client-context';
import type { NotificationsClient } from '@/api/notifications-client';
import { AnnouncementsAdminClientProvider } from '@/api/announcements-admin-client-context';
import type { AnnouncementsAdminClient } from '@/api/announcements-admin-client';
import { NotificationBannerProvider } from '@/components/notification-banner';
import {
  KbAdminClientProvider,
  KbSupplierClientProvider,
} from '@/api/kb-client-context';
import type { KbAdminClient, KbClient } from '@/api/kb-client';
import { AuthProvider, demoAuthState, type AuthState } from '@/auth/auth-context';
import { AuthBootstrap } from '@/auth/auth-bootstrap';

// =============================================================================
// providers.tsx — Composition des contextes applicatifs.
// =============================================================================
// Ordre : QueryClient (TanStack Query) > SupplierMeClient (résolution de rôle) > Auth (identité) >
// PingClient (double injectable) > … Les props permettent aux tests d'injecter un QueryClient neuf,
// une identité contrôlée et des clients API doublés.
//
// RÉSOLUTION DE L'IDENTITÉ (S0-ROLE, AuthGate) — trois chemins mutuellement exclusifs :
//   1. `auth` fourni en prop (TESTS / usages explicites) ⇒ STATIQUE : identité injectée telle quelle,
//      AUCUN I/O réseau (chemin de test synchrone et déterministe — DOIT rester intact).
//   2. `bootstrap` (PRODUCTION, main.tsx) ⇒ DYNAMIQUE : AuthBootstrap résout la session fournisseur
//      RÉELLE (whoami + me) et retombe sur l'interim admin (demoAuthState) si aucune session
//      (P-ROLE-INTERIM=a).
//   3. Ni l'un ni l'autre ⇒ interim admin STATIQUE (demoAuthState) : rétro-compatibilité des tests qui
//      montent AppProviders sans `auth` (fixtures admin historiques). Aucun réseau.

export function createQueryClient(): QueryClient {
  // INTERCEPTEUR 401 (L-2) — CHOKEPOINT UNIQUE de toutes les requêtes/mutations TanStack Query
  // (QueryCache + MutationCache onError). Un 401 sur une requête ADMIN post-auth (HttpApiError,
  // status 401) = session admin EXPIRÉE en cours d'usage (DBU §2.1 l.590/592) ⇒ retour PROPRE à la
  // landing (navigation pleine page → purge de l'état d'auth client, RULE_REF_006), AVANT tout rendu
  // d'erreur métier (aucune tuile en erreur). Séparation automatique : les sondes de bootstrap
  // (whoami/getMe/getAdminSession) sont appelées HORS TanStack Query ⇒ leur 401 attendu (« admin sans
  // session fournisseur ») n'atteint JAMAIS cet onError (pas de faux positif). Les 401 supplier
  // portent kind:'unauthorized' (pas HttpApiError) ⇒ hors périmètre, ignorés ici. Garde ANTI-LOOP :
  // jamais de redirect si déjà sur un chemin public (même prédicat partagé qu'au bootstrap).
  const onApiError = (error: unknown): void => {
    if (isUnauthorized(error) && !isPublicPath(window.location.pathname)) {
      window.location.assign('/');
    }
  };
  return new QueryClient({
    queryCache: new QueryCache({ onError: onApiError }),
    mutationCache: new MutationCache({ onError: onApiError }),
    defaultOptions: {
      queries: { retry: false, staleTime: 0 },
      mutations: { retry: false },
    },
  });
}

export interface AppProvidersProps {
  children: React.ReactNode;
  queryClient?: QueryClient;
  /**
   * Identité injectée (TESTS / usages explicites). Fournie ⇒ chemin STATIQUE sans I/O réseau. Absente
   * ⇒ voir `bootstrap` (production dynamique) ou interim admin statique (rétro-compat).
   */
  auth?: AuthState;
  /**
   * Active la résolution DYNAMIQUE de la session fournisseur (whoami + me) au montage — PRODUCTION
   * uniquement (main.tsx). Ignoré si `auth` est fourni. Défaut false : le chemin de test reste statique.
   */
  bootstrap?: boolean;
  /** Client de résolution du rôle de session (injectable pour les tests du bootstrap auth). */
  supplierMeClient?: SupplierMeClient;
  pingClient?: PingClient;
  parametersClient?: ParametersClient;
  identityClient?: IdentityClient;
  legalClient?: LegalClient;
  legalAdminClient?: LegalAdminClient;
  supplierClient?: SupplierClient;
  campaignClient?: CampaignClient;
  exportsClient?: ExportsClient;
  rateCollectionClient?: RateCollectionClient;
  operationalViewClient?: OperationalViewClient;
  adminActivationAlertsClient?: AdminActivationAlertsClient;
  positioningClient?: PositioningClient;
  rankAdminClient?: RankAdminClient;
  campaignAnalysisClient?: CampaignAnalysisClient;
  publicationClient?: PublicationClient;
  negotiationClient?: NegotiationClient;
  supplierDashboardClient?: SupplierDashboardClient;
  supplierHistoryClient?: SupplierHistoryClient;
  /** Client RÉCEPTION ANNONCES fournisseur (R15-COM-07) — onglet « Annonces JeKo reçues », doublable en test. */
  supplierAnnouncementsClient?: SupplierAnnouncementsClient;
  supplierConditionsClient?: SupplierConditionsClient;
  supplierTeamClient?: SupplierTeamClient;
  /** Clients CHAT (R15-COM-02) — deux seams distincts (profil admin / fournisseur), doublables en test. */
  chatAdminClient?: ChatClient;
  chatSupplierClient?: ChatClient;
  notificationsClient?: NotificationsClient;
  /** Client ANNONCES ADMIN (R15-COM-03) — surface composer/registre, doublable en test. */
  announcementsAdminClient?: AnnouncementsAdminClient;
  /** Clients KB (R15-COM-04) — admin (lecture+tags+export) / fournisseur (lecture seule), doublables. */
  kbAdminClient?: KbAdminClient;
  kbSupplierClient?: KbClient;
}

/**
 * Décide du chemin de résolution d'identité (statique injecté / bootstrap dynamique / interim admin
 * statique) et fournit l'AuthProvider correspondant. Voir l'entête de fichier (AuthGate, S0-ROLE).
 */
interface AuthGateProps {
  auth: AuthState | undefined;
  bootstrap: boolean;
  children: React.ReactNode;
}

function AuthGate({ auth, bootstrap, children }: AuthGateProps): React.JSX.Element {
  if (auth !== undefined) {
    return <AuthProvider value={auth}>{children}</AuthProvider>;
  }
  if (bootstrap) {
    return <AuthBootstrap>{children}</AuthBootstrap>;
  }
  return <AuthProvider value={demoAuthState}>{children}</AuthProvider>;
}

export function AppProviders({
  children,
  queryClient,
  auth,
  bootstrap = false,
  supplierMeClient,
  pingClient,
  parametersClient,
  identityClient,
  legalClient,
  legalAdminClient,
  supplierClient,
  campaignClient,
  exportsClient,
  rateCollectionClient,
  operationalViewClient,
  adminActivationAlertsClient,
  positioningClient,
  rankAdminClient,
  campaignAnalysisClient,
  publicationClient,
  negotiationClient,
  supplierDashboardClient,
  supplierHistoryClient,
  supplierAnnouncementsClient,
  supplierConditionsClient,
  supplierTeamClient,
  chatAdminClient,
  chatSupplierClient,
  notificationsClient,
  announcementsAdminClient,
  kbAdminClient,
  kbSupplierClient,
}: AppProvidersProps): React.JSX.Element {
  const [client] = React.useState(() => queryClient ?? createQueryClient());
  return (
    <QueryClientProvider client={client}>
      <NotificationsClientProvider client={notificationsClient}>
      <NotificationBannerProvider>
      <SupplierMeClientProvider client={supplierMeClient}>
        {/* IdentityClientProvider ENVELOPPE AuthGate : AuthBootstrap (L-3) consomme getAdminSession
            via useIdentityClient — il doit donc voir le client injecté AVANT la résolution d'identité,
            comme SupplierMeClientProvider pour whoami/getMe. Sert aussi les écrans descendants. */}
        <IdentityClientProvider client={identityClient}>
        <AuthGate auth={auth} bootstrap={bootstrap}>
          <PingClientProvider client={pingClient}>
          <ParametersClientProvider client={parametersClient}>
              <LegalClientProvider client={legalClient}>
                <LegalAdminClientProvider client={legalAdminClient}>
                  <SupplierClientProvider client={supplierClient}>
                    <CampaignClientProvider client={campaignClient}>
                      <ExportsClientProvider client={exportsClient}>
                        <RateCollectionClientProvider
                          client={rateCollectionClient}
                        >
                          <OperationalViewClientProvider
                            client={operationalViewClient}
                          >
                            <AdminActivationAlertsClientProvider
                              client={adminActivationAlertsClient}
                            >
                            <PositioningClientProvider
                              client={positioningClient}
                            >
                              <RankAdminClientProvider
                                client={rankAdminClient}
                              >
                                <CampaignAnalysisClientProvider
                                  client={campaignAnalysisClient}
                                >
                                  <PublicationClientProvider
                                    client={publicationClient}
                                  >
                                    <NegotiationClientProvider
                                      client={negotiationClient}
                                    >
                                      <SupplierDashboardClientProvider
                                        client={supplierDashboardClient}
                                      >
                                        <SupplierHistoryClientProvider
                                          client={supplierHistoryClient}
                                        >
                                          <SupplierAnnouncementsClientProvider
                                            client={supplierAnnouncementsClient}
                                          >
                                          <SupplierConditionsClientProvider
                                            client={supplierConditionsClient}
                                          >
                                            <SupplierTeamClientProvider
                                              client={supplierTeamClient}
                                            >
                                              <ChatAdminClientProvider
                                                client={chatAdminClient}
                                              >
                                                <ChatSupplierClientProvider
                                                  client={chatSupplierClient}
                                                >
                                                  <AnnouncementsAdminClientProvider
                                                    client={announcementsAdminClient}
                                                  >
                                                    <KbAdminClientProvider
                                                      client={kbAdminClient}
                                                    >
                                                      <KbSupplierClientProvider
                                                        client={kbSupplierClient}
                                                      >
                                                        {children}
                                                      </KbSupplierClientProvider>
                                                    </KbAdminClientProvider>
                                                  </AnnouncementsAdminClientProvider>
                                                </ChatSupplierClientProvider>
                                              </ChatAdminClientProvider>
                                            </SupplierTeamClientProvider>
                                          </SupplierConditionsClientProvider>
                                          </SupplierAnnouncementsClientProvider>
                                        </SupplierHistoryClientProvider>
                                      </SupplierDashboardClientProvider>
                                    </NegotiationClientProvider>
                                  </PublicationClientProvider>
                                </CampaignAnalysisClientProvider>
                              </RankAdminClientProvider>
                            </PositioningClientProvider>
                            </AdminActivationAlertsClientProvider>
                          </OperationalViewClientProvider>
                        </RateCollectionClientProvider>
                      </ExportsClientProvider>
                    </CampaignClientProvider>
                  </SupplierClientProvider>
                </LegalAdminClientProvider>
              </LegalClientProvider>
          </ParametersClientProvider>
          </PingClientProvider>
        </AuthGate>
        </IdentityClientProvider>
      </SupplierMeClientProvider>
      </NotificationBannerProvider>
      </NotificationsClientProvider>
    </QueryClientProvider>
  );
}
